<script setup lang="ts">
import { LucideLoader2 } from 'lucide-vue-next'
import type { ICommentFormData } from './types'

const { t } = useI18n()

const props = defineProps<{
  parentId?: number | null
  parentAuthor?: string
  onCancel?: () => void
  onSubmit?: (data: ICommentFormData) => Promise<boolean>
}>()

const emit = defineEmits<{
  submit: [data: ICommentFormData]
}>()

const formData = ref<ICommentFormData>({
  text: '',
  parent_id: props.parentId || null
})

const isSubmitting = ref(false)
const errors = ref<Record<string, string>>({})

const characterCount = computed(() => formData.value.text.length)
const maxCharacters = 1000
const isTextTooLong = computed(() => characterCount.value > maxCharacters)
const isValid = computed(() => {
  return formData.value.text.trim().length > 0 && !isTextTooLong.value
})

const handleSubmit = async () => {
  errors.value = {}

  if (!formData.value.text.trim()) {
    errors.value.text = t('widget.comment.form.text_required')
    return
  }

  if (characterCount.value > maxCharacters) {
    errors.value.text = t('widget.comment.form.text_too_long', { max: maxCharacters })
    return
  }

  isSubmitting.value = true

  try {
    let success: boolean

    if (props.onSubmit) {
      success = await props.onSubmit({ ...formData.value })
    } else {
      emit('submit', { ...formData.value })
      success = true
    }

    if (success) {
      formData.value.text = ''
    }
  } catch {
    errors.value.general = t('widget.comment.form.general_error')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <div
    :class="{
      'border-t border-gray-200 pt-6': !parentId,
      'rounded-lg border border-gray-200 bg-gray-50 p-3': parentId
    }"
  >
    <!-- Reply indicator -->
    <div v-if="parentId && parentAuthor" class="mb-3 flex items-center justify-between">
      <p class="text-sm text-gray-600">
        {{ $t('common.reply') }} <span class="font-medium">{{ parentAuthor }}</span>
      </p>
      <Button v-if="onCancel" type="submit" @click="onCancel">
        {{ $t('common.cancel') }}
      </Button>
    </div>

    <form
      v-webmcp-tool="{
        name: 'post_comment',
        description:
          'Post a comment under this page. The comment is published on the site.'
      }"
      class="space-y-3"
      @submit.prevent="handleSubmit"
    >
      <!-- Comment textarea -->
      <div>
        <Textarea
          id="comment_text"
          v-model="formData.text"
          :placeholder="$t('widget.comment.form.placeholder')"
          class="min-h-20 resize-none border-gray-300 focus:border-gray-400 focus:ring-0"
          :class="{
            'border-red-300': errors.text
          }"
        />
        <div class="mt-1 flex items-center justify-between">
          <p v-if="errors.text" class="text-xs text-red-600">{{ errors.text }}</p>
          <p v-else-if="errors.general" class="text-xs text-red-600">{{ errors.general }}</p>
          <span v-else />
          <span
            class="text-xs"
            :class="isTextTooLong ? 'text-red-600' : 'text-gray-500'"
          >{{ characterCount }}/{{ maxCharacters }}</span>
        </div>
      </div>

      <!-- Submit button -->
      <div class="flex justify-end">
        <Button type="submit" variant="blue" :disabled="!isValid || isSubmitting">
          <LucideLoader2 v-if="isSubmitting" class="size-4 animate-spin" />
          {{ isSubmitting ? $t('common.submitting') : $t('common.publish') }}
        </Button>
      </div>
    </form>
  </div>
</template>
